import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useGetProductList } from "@/api/client/product";
import { getSearchApi } from "@/api/end-points/search";
import { SortModal } from "@/components/actions";
import SortFilter from "@/components/actions/SortFilter";
import ProductCard from "@/components/data-display/card/product";
import CategoryHeading from "@/components/data-display/categoryHeading";
import { SortIcon } from "@/components/data-display/icon";
import Loader from "@/components/data-display/loaderSvg";
import { HeroFeedback } from "@/components/feedback";
import CategoriesMenu from "@/components/navigation/categories";
import Pagination from "@/components/navigation/pagination";
import Testimonials from "@/components/sections/Testimonials";
import MetaData from "@/components/seo/MetaData";
import Breadcrumbs from "@/components/ui/navigation/breadcrumbs";
import useRouterPush from "@/hooks/useRouterPush";
import { getWishlistState } from "@/store/feature/wishlistSlice";
import { getPageResult } from "@/utils/helpers/pageResult";
import { generateUrlToQuery } from "@/utils/helpers/queryString";
import { formatSortOptions } from "@/utils/helpers/sort";
import { updateListWithWishlistStatus } from "@/utils/helpers/wishlist";

function Search(data) {
  const [productList, setProductList] = useState(data.products);
  const [searchMeta, setSearchMeta] = useState(data.meta);
  const [categories, setCategories] = useState(data.categories);
  const [sortOptions, setSortOptions] = useState(data.sortables);
  const [isSortModal, setSortModal] = useState(false);

  const { wishlistItems } = useSelector(getWishlistState);
  const { queryParams, updateQueryParams } = useRouterPush();

  useEffect(() => {
    setSearchMeta(data.meta);
    setCategories(data.categories);
    setSortOptions(data.sortables);

    if (Object.keys(wishlistItems).length) {
      setProductList(
        updateListWithWishlistStatus(data?.products, wishlistItems)
      );
    } else {
      setProductList(data?.products);
    }
  }, [queryParams.query]);

  useEffect(() => {
    if (!Object.keys(wishlistItems).length) return;

    setProductList(updateListWithWishlistStatus(productList, wishlistItems));
  }, [Object.keys(wishlistItems).length]);

  const { isFetching: isProductFetching } = useGetProductList(
    generateUrlToQuery("", queryParams),
    {
      enabled: Object.keys(queryParams).length > 1,
      onSuccess: (data) => {
        setSearchMeta(data?.meta);

        if (Object.keys(wishlistItems).length) {
          setProductList(
            updateListWithWishlistStatus(data?.products, wishlistItems)
          );
        } else {
          setProductList(data?.products);
        }
      },
    }
  );

  if (!isProductFetching && !productList.length) {
    return (
      <div className="container flex h-[75vh] items-center justify-center">
        <HeroFeedback
          title="Sorry! No results found :("
          description="We couldn’t find what you searched for. Try searching again with something else."
          imageAltText="No Search Result Image"
          imageURL="/assets/images/no-results.png"
        />
      </div>
    );
  }

  return (
    <>
      <MetaData {...data.metadata} schema={data.schema} />

      {isProductFetching && <Loader />}

      <div className="container pb-[1rem]">
        <div className="mt-[1.375rem]">
          <Breadcrumbs
            breadcrumbs={[
              { link: "", value: "Home" },
              { link: "", value: "Search Result" },
            ]}
          />
        </div>
        <section className="flex justify-between lg:space-x-[70px]">
          <div className="hidden w-[17%] pt-[2.563rem] lg:block">
            <p className="mb-9 text-grayDisplay-300">All Categories</p>
            <CategoriesMenu categoryList={categories} />
          </div>

          <div className="w-full pt-[1.313rem] lg:w-[80%]">
            <CategoryHeading
              categoryName={`Search Results ${
                queryParams.query && `for "${queryParams.query}"`
              }`}
            />

            <div className="my-[2rem] flex items-center justify-between">
              <p>{getPageResult(searchMeta)}</p>
              <SortFilter sortOptions={sortOptions} />

              <div className="flex h-[2.5rem] w-[9.5rem] cursor-pointer items-center justify-center rounded-[2.625rem] bg-[#EBEEF2] md:hidden">
                <label
                  type="button"
                  onClick={() => setSortModal(true)}
                  className="flex w-full items-center justify-center gap-2 text-center text-[1.1rem] text-[#0A101A]"
                >
                  <SortIcon />
                  <span>Sort By</span>
                </label>
              </div>
            </div>

            {productList.length > 0 && (
              <div className="grid grid-cols-2 gap-[0.75rem] lg:grid-cols-3 lg:gap-[1.563rem] xl:grid-cols-4">
                {productList.map((product) => (
                  <ProductCard product={product} key={product.slug} />
                ))}
              </div>
            )}

            {searchMeta.total > searchMeta.per_page && (
              <div className="mt-[4.563rem] lg:mt-[5.938rem]">
                <Pagination
                  initialPage={searchMeta.current_page - 1}
                  getPaginateData={(selectedPage) =>
                    updateQueryParams({ page: selectedPage + 1 })
                  }
                  totalPage={Math.ceil(searchMeta.total / searchMeta.per_page)}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      {data?.testimonials?.length > 0 && (
        <Testimonials data={data?.testimonials} className="lg:!pt-[2.125rem]" />
      )}

      <SortModal
        isOpen={isSortModal}
        onHide={() => setSortModal(false)}
        value={{ value: queryParams.sort_by }}
        sortData={formatSortOptions(sortOptions)}
        onChange={(e) => {
          updateQueryParams({ sort_by: e.target.value, page: 1 });
          setSortModal(false);
        }}
      />
    </>
  );
}

export async function getServerSideProps(context) {
  const query = context.query.query;
  const queryString = `?query=${query}`;
  const res = await getSearchApi(queryString);

  if (!res?.data?.data) {
    return {
      notFound: true,
    };
  }

  return { props: res?.data?.data };
}

export default Search;
